'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { trackCTAClick } from '../../../lib/analytics';

/**
 * The hero's two calls to action.
 *
 * Split out of HeroSection purely to keep the click handlers behind their own
 * 'use client' boundary, so the hero itself stays free of client state.
 *
 * Two buttons rather than the brief's one. That is the agreed design override
 * recorded on epic #7918: the reference carries a primary and a secondary and
 * the secondary is what gives a visitor who is not ready to book a call
 * somewhere to go.
 *
 * next/link, never a bare <a>: internal navigation through a raw anchor is a
 * full page reload and is flagged by check-ui-patterns.sh.
 *
 * /ai-adoption is built by #7922. The link ships now, per the ticket.
 *
 * The labels are held on one line only from 480px up. Below that the buttons
 * stack full width, and "See how an engagement works" is wider than a 320px
 * viewport's content box, so holding it on one line would clip the label
 * against the hero's overflow: hidden. Letting it wrap costs a second line and
 * keeps every word readable.
 *
 * ATTENTION CUE. The primary button plays one light sweep (.cta-cue in
 * globals.css) a beat after the page settles, and never again. It is a single
 * pass rather than a pulse or a loop on purpose: the whole proposition here is
 * calm judgment, and a button that keeps asking for attention argues against
 * it. The delay is what makes it read as a highlight rather than as part of
 * page load, and it lets the hero's own convergence graphic land first.
 *
 * Both buttons take the hover and focus-visible lift, because lifting only one
 * of an adjacent pair reads as one of them being broken. Only the primary
 * takes the sweep.
 *
 * The cue is state rather than a plain CSS animation-delay so it cannot replay
 * on a re-render, and so reduced motion resolves it in CSS alone.
 */

/** Long enough for the hero to have settled, short enough to still belong to it. */
const CUE_DELAY_MS = 1100;

export default function HeroCtas() {
  const [cuePlayed, setCuePlayed] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setCuePlayed(true), CUE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className="flex flex-col items-stretch gap-[18px] pt-8 min-[480px]:flex-row min-[480px]:flex-wrap min-[480px]:items-center">
      <Link
        href="/contact"
        onClick={() => trackCTAClick('hero_start_conversation', '/contact')}
        data-cue={cuePlayed ? 'play' : undefined}
        className="cta-lift cta-cue inline-flex shrink-0 items-center justify-center min-[480px]:whitespace-nowrap rounded-lg bg-ink-blue px-[30px] py-4 font-sans text-base font-semibold text-on-blue no-underline"
      >
        Start a Conversation
      </Link>
      <Link
        href="/ai-adoption"
        onClick={() => trackCTAClick('hero_see_engagement', '/ai-adoption')}
        className="cta-lift inline-flex items-center justify-center min-[480px]:whitespace-nowrap rounded-lg bg-sand-accent px-[26px] py-4 font-sans text-base font-semibold text-sand-accent-text no-underline"
      >
        See how an engagement works
      </Link>
    </div>
  );
}
